import { pickUserFieldsData, addScopePrefix } from './userHelpers';

/**
 * Everything in the signup form that is not one of the hosted custom user fields. Those are the
 * template's own extras (a referral code, say) and belong in protectedData unchanged.
 *
 * Exported because the IdP confirm form on AuthenticationPage builds its own params — it carries an
 * idp token and has no password — but must split scopes by exactly the same rule. Two copies of
 * this would diverge silently: the difference only shows up when a field goes missing on one of
 * the two signup paths.
 */
export const getNonUserFieldParams = (values, userFieldConfigs = []) => {
  const userFieldKeys = userFieldConfigs.map(({ scope, key }) => addScopePrefix(scope, key));

  return Object.entries(values).reduce(
    (picked, [key, value]) => (userFieldKeys.includes(key) ? picked : { ...picked, [key]: value }),
    {}
  );
};

/**
 * Turn SignupForm values into the params the Marketplace API expects.
 *
 * Extracted from AuthenticationPage so the login dialog can register a visitor without sending
 * them to /signup first. Registration used to be the one thing that still left that dialog, and
 * it left it at the worst possible moment: mid-booking, right after the visitor picked dates and
 * pressed the CTA. Both surfaces now build their params here, so they cannot drift apart on which
 * scope a custom field lands in — a drift that would be invisible until a field went missing on
 * one of the two paths.
 *
 * `terms` is deliberately NOT stripped: the stock form has always carried the accepted checkbox
 * into protectedData, and that record is the marketplace's evidence that this user agreed to the
 * terms. Dropping it here would have silently ended that trail for everyone who signs up.
 *
 * @param {Object} values raw SignupForm values
 * @param {Array} userFields hosted custom user field configs
 * @returns {Object} params for ducks/auth.duck signup()
 */
export const buildSignupParams = (values, userFields = []) => {
  const { userType, email, password, fname, lname, displayName, ...rest } = values || {};
  const displayNameMaybe = displayName ? { displayName: displayName.trim() } : {};

  return {
    email,
    password,
    firstName: fname?.trim(),
    lastName: lname?.trim(),
    ...displayNameMaybe,
    publicData: {
      userType,
      ...pickUserFieldsData(rest, 'public', userType, userFields),
    },
    privateData: {
      ...pickUserFieldsData(rest, 'private', userType, userFields),
    },
    protectedData: {
      ...pickUserFieldsData(rest, 'protected', userType, userFields),
      ...getNonUserFieldParams(rest, userFields),
    },
  };
};
